import styles from "@/components/settings-item.module.css";
import Switch from "@/components/switch";
import {
	GeneralSettingsInfo,
	OptionInfo,
	SettingsItemInfo,
	SettingsItemType,
	SettingsItemWarning,
} from "@/types";
import { showDialog, updateSettings } from "@/utils";
import {
	faCaretDown,
	faCheck,
	faWarning,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, MouseEvent } from "react";

interface SettingsItemProps {
	item: SettingsItemInfo;
	openDropdownMenu: (
		event: MouseEvent<HTMLButtonElement>,
		content: OptionInfo[],
		key: string,
		onChange?: SettingsItemInfo["onChange"],
	) => void;
}

function SettingsItem({
	item,
	openDropdownMenu,
}: SettingsItemProps): JSX.Element {
	if (item.type === SettingsItemType.GROUP_TITLE) {
		return <div className={styles["group-title"]}>{item.title}</div>;
	}

	const colorElements = item.colors?.map((color, index) => {
		const isSelected = color.value === item.selectedValue;

		const handleColorOptionClick = async (): Promise<void> => {
			await updateSettings({
				themeColor: color.value as GeneralSettingsInfo["themeColor"],
			});
		};

		return (
			<button
				aria-checked={isSelected}
				className={styles["color-option"]}
				key={index}
				onClick={() => void handleColorOptionClick()}
				role="radio"
				style={{ backgroundColor: color.color }}
				title={color.label}
				type="button"
			>
				{isSelected && <FontAwesomeIcon icon={faCheck} />}
			</button>
		);
	});

	const selectedValue =
		(item.selectedValue && item.labels?.[item.selectedValue]) ||
		item.selectedValue;

	const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
		switch (item.type) {
			case SettingsItemType.BUTTON: {
				void item.onClick?.(event);
				break;
			}
			case SettingsItemType.SELECT: {
				if (!item.options || !item.key) {
					return;
				}
				openDropdownMenu(
					event,
					item.options.map((option) => {
						return {
							text: item.labels?.[option] || option,
							value: option,
						};
					}),
					item.key,
					item.onChange,
				);
				break;
			}
			default:
				break;
		}
	};

	const handleWarningClick = (): void => {
		switch (item.warning) {
			case SettingsItemWarning.BROWSER_INCOMPATIBLE: {
				void showDialog(t("browserIncompatibleDescription"));
				break;
			}
			case SettingsItemWarning.CONTROLLED_BY_EXTENSION: {
				void showDialog(t("controlledByExtensionDescription"));
				break;
			}
			case SettingsItemWarning.INVALID: {
				void showDialog(t("invalidDescription"));
				break;
			}
			default:
				break;
		}
	};

	return (
		<div
			className={clsx(
				styles["settings-item"],
				item.warning !== undefined && styles["invalid"],
				item.isMasterReset && styles["master-reset"],
				item.shouldWrapOnMobile && styles["wrap-on-mobile"],
			)}
		>
			<div>
				{item.warning !== undefined && (
					<button
						className={styles["warning"]}
						onClick={handleWarningClick}
						type="button"
					>
						<FontAwesomeIcon icon={faWarning} />
						{
							{
								[SettingsItemWarning.BROWSER_INCOMPATIBLE]: t(
									"browserIncompatible",
								),
								[SettingsItemWarning.CONTROLLED_BY_EXTENSION]:
									t("controlledByExtension"),
								[SettingsItemWarning.INVALID]: t("invalid"),
							}[item.warning]
						}
					</button>
				)}
				<div>{item.title}</div>
				{item.description && (
					<div className={styles["settings-description"]}>
						{item.description}
					</div>
				)}
			</div>
			{item.type === SettingsItemType.SWITCH && (
				<Switch
					isChecked={item.isChecked || false}
					isDisabled={item.warning !== undefined}
					onChange={(event) => {
						void item.onChange?.(event.target.checked);
					}}
					title={item.title}
				/>
			)}
			{item.type === SettingsItemType.SELECT && (
				<button
					aria-haspopup="listbox"
					aria-label={`${item.title} ${selectedValue}`}
					className={styles["select"]}
					onClick={handleClick}
					type="button"
				>
					{selectedValue}
					<FontAwesomeIcon icon={faCaretDown} />
				</button>
			)}
			{item.type === SettingsItemType.BUTTON && (
				<button
					aria-label={item.title}
					className={styles["button"]}
					onClick={handleClick}
					type="button"
				>
					{item.buttonText}
				</button>
			)}
			{item.type === SettingsItemType.COLOR && (
				<div className={styles["color-options-container"]}>
					{colorElements}
				</div>
			)}
		</div>
	);
}

export default SettingsItem;
